import { useEffect, useState, useSyncExternalStore } from "react";
import MaterialIcon from "@common/components/MaterialIcon";
import {
  assignKey,
  bindings,
  currentOverrides,
  isCustomised,
  keyLabel,
  movesOf,
  onKeymapChange,
  resetKeys,
  setCapturing,
  turboKey,
  type Layout,
} from "../keymap";

/** Which key is being chosen: a button's slot, or the turbo key. */
interface Picking {
  button: string | null;
  slot: number;
}

/**
 * The keyboard for a console, from the shared layout and the player's
 * changes: one row per button. Click a key to press a new one for it
 * (Escape keeps the old), or add one; Reset returns to the shared layout.
 */
export default function KeyHelp({ system }: { system: Layout }) {
  useSyncExternalStore(onKeymapChange, currentOverrides);
  const [picking, setPicking] = useState<Picking | null>(null);

  // The next key pressed is the answer. Modifiers alone don't count.
  useEffect(() => {
    if (!picking) return;
    setCapturing(true);
    const onKey = (e: KeyboardEvent) => {
      e.preventDefault();
      e.stopImmediatePropagation();
      if (/^(Shift|Control|Alt|Meta)/.test(e.code)) return;
      if (e.code !== "Escape") assignKey(system, picking.button, picking.slot, e.code);
      setPicking(null);
    };
    window.addEventListener("keydown", onKey, true);
    return () => {
      window.removeEventListener("keydown", onKey, true);
      setCapturing(false);
    };
  }, [picking, system]);

  const rows = bindings(system);
  const key = (button: string | null, slot: number, code: string | undefined) => {
    const active = picking?.button === button && picking.slot === slot;
    return (
      <button
        key={slot}
        type="button"
        className={`key${active ? " picking" : ""}${code ? "" : " add"}`}
        title={code ? "Press a key to change this" : "Add a key"}
        onClick={() => setPicking(active ? null : { button, slot })}
      >
        {active ? "Press a key…" : code ? <kbd>{keyLabel(code)}</kbd> : <MaterialIcon name="add" />}
      </button>
    );
  };

  return (
    <div className="key-help">
      <ul>
        {rows.map(({ button, keys }) => (
          <li key={button}>
            <span className="key-name">{button === "Up" || button === "Down" || button === "Left" || button === "Right" ? `${button} (${movesOf(system)})` : button}</span>
            <span className="key-caps">
              {keys.map((code, slot) => key(button, slot, code))}
              {keys.length < 3 && key(button, keys.length, undefined)}
            </span>
          </li>
        ))}
        <li>
          <span className="key-name">Fast-forward (hold)</span>
          <span className="key-caps">{key(null, 0, turboKey() || undefined)}</span>
        </li>
      </ul>
      {isCustomised(system) && (
        <button type="button" className="btn small" onClick={() => resetKeys(system)}>
          Reset to the shared layout
        </button>
      )}
    </div>
  );
}
